import { useAsyncExecutor2 } from "async-executor-hook";
import { Menu } from "primereact/menu";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import {
  catchError,
  filter,
  from,
  interval,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from "rxjs";
import { useScroll$ } from "../../common/hooks/useScroll$";
import { getTimeLabel } from "../../common/utils";
import IcNotificationIconMenu from "../../images/ic_icon_delete_notification.svg";
import {
  HTTP_METHODS,
  NOTIFICATION_SECTION_POOL_INTERVAL,
} from "../../utils/constants";
import { networkBackendRequest } from "../../utils/networkUtils";
import { RequestState } from "../../utils/request.model";
import PaymentLoader from "../PaymentLoader/PaymentLoader";
import {
  DivUserMenu,
  DivUserMenuLabel,
} from "../UsersManagementAdmin/UsersManagementAdminStyles";
import { RequestPaged } from "./model";
import {
  DivContentNotification,
  IconNotificationDiv,
  NotificationDiv,
} from "./SidebarNotificationsStyles";

import LoadingGray from "../../images/loading_gray.gif";
import { NotificationLoading } from "./CommonNotificationStyles";
import { Authorization } from "../../common/AuthorizationCpt";
interface ICommonNotification<N> {
  url: string;
  buildDeleteUrl: (notificationId: string) => string;
  redirectOnClick: (notification: N) => void;
  notificationTemplate: (props: { notification: N }) => ReactNode;
  authorization: string;
  loadCountNotification$: Subject<void>;
}

export type ICommonNotificationResponse<T> = {
  notifications: T[];
} & RequestPaged;

export type ICommonNotificationResponseState<T> = RequestState<
  ICommonNotificationResponse<T>
>;

export const CommonNotification = <N extends { notification_id: string }>(
  props: ICommonNotification<N>
) => {
  const {
    url,
    notificationTemplate,
    buildDeleteUrl,
    redirectOnClick,
    authorization,
    loadCountNotification$,
  } = props;
  const containerRef = useRef();
  const destroy$Ref = useRef(new Subject<void>());

  const forceUpdate$Ref = useRef<Subject<void>>(new Subject());
  const forceUpdate$ = forceUpdate$Ref.current;

  const destroy$ = destroy$Ref.current;

  const scroll$ = useScroll$(containerRef.current);

  const [notificationsState, setNotificationsState] = useState<
    ICommonNotificationResponseState<N>
  >({
    data: {
      notifications: [],
      num_pages: 1,
      page: 1,
      page_count: 1,
      page_size: 1,
      total_count: 1,
    },
  });

  useEffect(() => {
    let total_pages = 1;
    let pageSize = 0;
    const pageSize$ = scroll$.pipe(
      filter((scroll: any) => {
        const { scrollTop, scrollHeight, viewportHeight } = scroll;
        return scrollTop >= scrollHeight * 0.9 - viewportHeight;
      }),
      startWith(null),
      takeWhile((_, i) => i < total_pages),
      map((_, i) => (i + 1) * 10),
      tap((page_size) => {
        pageSize = page_size;
      })
    );
    merge(pageSize$, forceUpdate$.pipe(map(() => pageSize)))
      .pipe(
        switchMap((pageSize) => {
          const notificationsState$: Observable<
            ICommonNotificationResponseState<N>
          > = interval(NOTIFICATION_SECTION_POOL_INTERVAL).pipe(
            takeUntil(destroy$),
            startWith(null),
            switchMap(() => {
              const request = networkBackendRequest(url, HTTP_METHODS.GET, {
                page_size: pageSize,
              }).then((response) => response.json());

              return from(request).pipe(
                tap((resp) => {
                  total_pages = resp.num_pages;
                }),
                map((resp: any): ICommonNotificationResponseState<N> => {
                  return { status: "SUCCESS", data: resp };
                }),
                catchError((error) => {
                  return of<ICommonNotificationResponseState<N>>({
                    status: "FAILED",
                    error,
                  });
                })
              );
            })
          );
          return merge(
            of<ICommonNotificationResponseState<N>>({
              status: "PROCESSING",
              data: {
                notifications: [],
                num_pages: 1,
                page: 1,
                page_count: 1,
                page_size: 1,
                total_count: 1,
              },
            }),
            notificationsState$
          );
        })
      )
      .subscribe((notificationsState) => {
        setNotificationsState((oldState) => {
          if (notificationsState.status === "PROCESSING") {
            return { ...oldState, status: "PROCESSING" };
          }
          return notificationsState;
        });
      });
  }, [destroy$, forceUpdate$, scroll$, url]);

  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  return (
    <>
      <div
        className="scroll card-style"
        ref={containerRef}
        style={{ borderRadius: 8, marginBottom: "1rem" }}
      >
        <div>
          {notificationsState.data.notifications.map((notification, i) => {
            const noti: any = notification;
            const obj: any =
              noti.dam || noti.invoice || noti.appointment || noti.volante;
            const timeAgo = getTimeLabel(obj.reference_time);
            const agent_name = obj.agent_name;
            return (
              <>
                <DivContentNotification
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    redirectOnClick(notification);
                  }}
                >
                  <div>
                    <NotificationDiv
                      fontWeight={500}
                      fontSize="1.1rem"
                      color="#2E353D"
                      paddingLeft="1rem"
                      paddingTop="1.3rem"
                    >
                      Hace {timeAgo}
                    </NotificationDiv>
                    <div style={{ display: "flex" }}>
                      <div style={{ width: "29rem" }}>
                        <NotificationDiv
                          fontWeight={800}
                          fontSize="1.3rem"
                          marginTop="0.5rem"
                          paddingLeft="1rem"
                          color="#2E353D"
                        >
                          {agent_name}
                        </NotificationDiv>
                      </div>
                      <Authorization actions={authorization}>
                        <RemoveNotificationOption
                          buildDeleteUrl={buildDeleteUrl}
                          forceUpdate$={forceUpdate$}
                          notificationId={notification.notification_id}
                          loadCountNotification$={loadCountNotification$}
                        />
                      </Authorization>
                    </div>
                    <div style={{ cursor: "pointer" }}>
                      {notificationTemplate({
                        notification,
                      })}
                    </div>
                  </div>
                </DivContentNotification>
              </>
            );
          })}
        </div>
      </div>
      {notificationsState.status === "PROCESSING" && (
        <div
          style={{ width: "100%", paddingRight: "2rem", paddingLeft: "2rem" }}
        >
          <PaymentLoader numItems={3} />
        </div>
      )}
    </>
  );
};

interface IRemoveNotificationOption {
  forceUpdate$: Subject<void>;
  buildDeleteUrl: (notificationId: string) => string;
  notificationId: string;
  loadCountNotification$: Subject<void>;
}

const RemoveNotificationOption: FC<IRemoveNotificationOption> = (props) => {
  const {
    forceUpdate$,
    notificationId,
    loadCountNotification$,
    buildDeleteUrl,
  } = props;

  const deleteNotificationExecutor = useAsyncExecutor2(
    (params: any) => {
      const { deleteurl } = params;
      return networkBackendRequest(deleteurl, HTTP_METHODS.PATCH, null, {});
    },
    {
      onSuccess: (_, params) => {
        const { event } = params;
        forceUpdate$.next();
        loadCountNotification$.next();
        menuRef.current.toggle(event);
      },
    }
  );

  const menuRef = useRef<any>();
  const items = [
    {
      label: "Delete",
      icon: "pi pi-times",
      template: function Save(item, options) {
        return (
          <>
            {deleteNotificationExecutor.status === "PROCESSING" && (
              <NotificationLoading>
                <img
                  src={LoadingGray}
                  alt=""
                  className="icon-loading-notification"
                />
              </NotificationLoading>
            )}
            {deleteNotificationExecutor.status !== "PROCESSING" && (
              <DivUserMenu
                marginTop="0.3rem"
                marginLeft="0.3rem"
                marginBottom="0.3rem"
                key={item}
                className={options.className}
                onClick={(event) => {
                  event.stopPropagation();
                  const deleteurl = buildDeleteUrl(notificationId);
                  deleteNotificationExecutor.execute({
                    deleteurl,
                    notification_id: notificationId,
                    event,
                  });
                }}
              >
                <span>
                  <img
                    src={IcNotificationIconMenu}
                    height="20px"
                    width="20px"
                    alt=""
                  />
                </span>
                <DivUserMenuLabel
                  fontSize="1.2rem"
                  marginLeft="1.5rem"
                  fontWeight="600"
                >
                  Eliminar esta notificación
                </DivUserMenuLabel>
              </DivUserMenu>
            )}
          </>
        );
      },
    },
  ];

  return (
    <IconNotificationDiv marginRight="1rem">
      <Menu
        style={{ width: "28.2rem", borderRadius: "8px" }}
        model={items}
        popup
        ref={(elRef) => (menuRef.current = elRef)}
        id="popup_menu_two"
      />
      <div
        className="img-dots"
        onClick={(event) => {
          event.stopPropagation();
          menuRef.current.toggle(event);
        }}
        style={{ width: "2rem", height: "2rem" }}
      ></div>
    </IconNotificationDiv>
  );
};
